// ── 계약 모달 — 계약정보 섹션 (계약상품 + 서비스상품 + 계약상태) ──────────────
import React from 'react';
import { FormField, Req } from './utils';
import { isFieldVisible, isRequired, unitOf, showValidity } from './contractFieldMeta';
import {
  sectionCard, sectionTitleBar, sectionDot,
  chipRowStyle, chipBtn,
  countListBox, countRow, colorDot, countLabel, countNumInput, unitLabel,
  serviceZone, serviceZoneTitle, optionalBadge,
} from './modalStyles';

export function ProductsSection({ form, errors, setField, ticketTypes, toggleProduct, setCount, setValidity, isNew, isReorder, cfg = null }) {
  // 설정이 없으면 지금 화면 그대로 — 무회귀
  const vis = (id) => (cfg ? isFieldVisible(cfg, 'contract', id, { isNew }) : true);
  const lab = (id, name) => {
    const req = cfg ? isRequired(cfg, id) : ['contract_date', 'contractProducts'].includes(id);
    return req ? <Req label={name} /> : name;
  };
  const unit = (id) => (cfg ? unitOf(cfg, id) : '장');
  const months = (id) => (cfg ? showValidity(cfg, id) : true);
  return (
    <div style={sectionCard}>
      {/* 계약 정보 헤더 + 신규/재주문 토글 */}
      <div style={{ ...sectionTitleBar('#16a34a'), justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
          <span style={sectionDot('#16a34a')} />
          계약 정보
        </div>
        {/* 신규/재주문 선택 — 필수, 한 번 선택 후 취소 불가 (다른 것 선택만 가능) */}
        {vis('registrationType') && <div style={{
          display: 'flex', gap: 4,
          outline: errors.registrationType ? '1.5px solid #ef4444' : 'none',
          borderRadius: 8, padding: errors.registrationType ? '2px' : 0,
        }}>
          {[
            { type: 'new',     label: '신규',   color: '#2563eb' },
            { type: 'reorder', label: '재주문', color: '#d97706' },
          ].map(({ type, label, color }) => {
            const active = form.registrationType === type;
            return (
              <button
                key={type}
                type="button"
                onClick={() => setField('registrationType', type)}  // 이미 선택된 버튼 클릭해도 유지
                style={{
                  padding: '2px 10px', borderRadius: 6, fontSize: 11, fontWeight: 700,
                  border: `1.5px solid ${active ? color : errors.registrationType ? '#ef4444' : 'var(--border)'}`,
                  background: active ? color : errors.registrationType ? 'rgba(239,68,68,0.06)' : 'var(--bg-card)',
                  color: active ? '#fff' : 'var(--text-sub)',
                  cursor: 'pointer', lineHeight: 1.6,
                }}
              >
                {label}
              </button>
            );
          })}
        </div>}
      </div>

      {/* 계약 상태 — 기본은 수정 모달에서만. ⚙️ 설정에서 "수정할 때만"을 끄면 신규에도 보인다 */}
      {vis('contract_status') && !isReorder && (
        <FormField label="계약 상태">
          <div style={{ display: 'flex', gap: 8 }}>
            {[
              { value: 'normal',    label: '계약완료', color: '#16a34a' },
              { value: 'cancelled', label: '계약취소', color: '#dc2626' },
            ].map(({ value, label, color }) => {
              const active = form.contract_status === value;
              return (
                <button
                  key={value}
                  type="button"
                  onClick={() => setField('contract_status', value)}
                  style={{
                    padding: '5px 14px', borderRadius: 6,
                    border: `1.5px solid ${active ? color : 'var(--border)'}`,
                    background: active ? color : 'var(--bg-card)',
                    color: active ? '#fff' : 'var(--text-sub)',
                    fontWeight: active ? 700 : 400, fontSize: 12, cursor: 'pointer',
                  }}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </FormField>
      )}

      {/* 계약일자 */}
      {vis('contract_date') && <FormField label={lab('contract_date', '계약일자')}>
        <div style={{ maxWidth: 200 }}>
          <input type="date" style={{ border: `1px solid ${errors.contract_date ? '#ef4444' : 'var(--border)'}`, borderRadius: 9, padding: '7px 9px', fontSize: 11, outline: 'none', width: '100%', boxSizing: 'border-box', color: 'var(--text)', background: errors.contract_date ? 'rgba(239,68,68,0.08)' : 'var(--bg-card)' }}
            value={form.contract_date}
            onChange={(e) => setField('contract_date', e.target.value)} />
        </div>
      </FormField>}

      {/* 계약 상품 */}
      {vis('contractProducts') && <FormField label={lab('contractProducts', '계약 상품')}>
        <div style={{
          ...chipRowStyle,
          ...(errors.contractProducts ? { outline: '1.5px solid #ef4444', borderRadius: 8, padding: 4 } : {}),
        }}>
          {ticketTypes.map((t) => {
            const name = t.name || t;  // DB 객체({ name, category }) 또는 문자열 모두 호환
            const on   = form.contractProducts.includes(name);
            return (
              <button key={name} type="button" style={chipBtn(on, '#16a34a')}
                onClick={() => toggleProduct('contract', name)}>
                {on && <span style={{ marginRight: 4, fontSize: 10 }}>✓</span>}{name}
              </button>
            );
          })}
        </div>
        {/* 계약장수 + 유효기간 입력
            장수는 필수가 아니라 빨간 테두리·경고문을 뗐다(2026-09-22). 비우면 0장으로 저장된다. */}
        {form.contractProducts.length > 0 && (
          <div style={countListBox}>
            {form.contractProducts.map((name) => (
              <div key={name} style={countRow}>
                <span style={colorDot('#16a34a')} />
                <span style={countLabel}>{name}</span>
                <input type="text" inputMode="numeric" pattern="[0-9]*"
                  style={{
                    ...countNumInput,
                    border: '1px solid var(--border)',
                  }}
                  placeholder="0"
                  value={form.contractCounts[name] || ''}
                  onChange={(e) => setCount('contract', name, e.target.value)} />
                <span style={unitLabel}>{unit('contractProducts')}</span>
                {months('contractProducts') && (<>
                  <input type="text" inputMode="numeric" pattern="[0-9]*"
                    style={{ ...countNumInput, width: 38, marginLeft: 6 }}
                    placeholder="0"
                    value={form.contractValidities[name] || ''}
                    onChange={(e) => setValidity('contract', name, e.target.value)} />
                  <span style={unitLabel}>개월</span>
                </>)}
              </div>
            ))}
          </div>
        )}
      </FormField>}

      {/* 서비스 상품 — 파란 배경 구역으로 구분 */}
      {vis('serviceProducts') && <div style={serviceZone}>
        <div style={serviceZoneTitle}>
          서비스 상품
          <span style={optionalBadge}>선택</span>
        </div>
        <div style={chipRowStyle}>
          {ticketTypes.map((t) => {
            const name = t.name || t;
            const on   = form.serviceProducts.includes(name);
            return (
              <button key={name} type="button" style={chipBtn(on, '#1d4ed8')}
                onClick={() => toggleProduct('service', name)}>
                {on && <span style={{ marginRight: 4, fontSize: 10 }}>✓</span>}{name}
              </button>
            );
          })}
        </div>
        {form.serviceProducts.length > 0 && (
          <div style={countListBox}>
            {form.serviceProducts.map((name) => (
              <div key={name} style={countRow}>
                <span style={colorDot('#1d4ed8')} />
                <span style={countLabel}>{name}</span>
                <input type="text" inputMode="numeric" pattern="[0-9]*" style={countNumInput}
                  placeholder="0"
                  value={form.serviceCounts[name] || ''}
                  onChange={(e) => setCount('service', name, e.target.value)} />
                <span style={unitLabel}>{unit('serviceProducts')}</span>
                {months('serviceProducts') && (<>
                  <input type="text" inputMode="numeric" pattern="[0-9]*"
                    style={{ ...countNumInput, width: 38, marginLeft: 6 }}
                    placeholder="0"
                    value={form.serviceValidities[name] || ''}
                    onChange={(e) => setValidity('service', name, e.target.value)} />
                  <span style={unitLabel}>개월</span>
                </>)}
              </div>
            ))}
          </div>
        )}
      </div>}
    </div>
  );
}
